<template>
    <div>
        <img
            :src="value.src"
            :width="imageSize.width"
            :height="imageSize.height"
            :srcset="srcset?.replace(/, ,/gi, ', ')"
            ref="img"
        />
    </div>
</template>

<style scoped>
img {
    width: 100%;
    height: auto;
}
</style>

<script setup lang="ts">
import {
    computed,
    getCurrentInstance,
    nextTick,
    onMounted,
    type PropType,
    ref,
} from "vue";
import { getWindowInnerWidth } from "../../../../utilities/browser-size";
import { type CustomImageModel } from "./types";
import { patchSrcSet } from "../../frontend-productlist/product-list/list-item-component/common/patch-src-set";
import fastdom from "fastdom";
import FastdomPromised from "fastdom/extensions/fastdom-promised";

const props = defineProps({
    value: {
        type: Object as PropType<CustomImageModel>,
        required: true,
    },
});

const imageWidth = 800;
const imageHeight = 384;

const unmodifiedSrcSet = computed(() => {
    return props.value?.srcSet ?? "";
});

const srcset = computed(() => {
    return (
        patchSrcSet(
            unmodifiedSrcSet.value,
            undefined,
            actualScale.value,
            props.value?.src ?? ""
        ) ?? undefined
    );
});

const instance = getCurrentInstance();

const img = ref<Node>();

const fd = fastdom.extend(FastdomPromised);

onMounted(() => {
    if (instance == null) throw new Error("missing instance");
    rootResizeObserver.value = new ResizeObserver((entries) => {
        const el = entries[0].target as HTMLDivElement;
        fd.measure(() => {
            return el.offsetWidth;
        }).then((newWidth) => {
            fd.mutate(() => {
                elWidth.value = newWidth;
            });
        });
    });
    const rootResizeObserver2 = rootResizeObserver.value;
    waitForNodePresent(
        "$el",
        () => instance?.proxy.$el,
        (node) => rootResizeObserver2.observe(node)
    );
    imageResizeObserver.value = new ResizeObserver((entries) => {
        const img = entries[0].target as HTMLImageElement;
        fd.measure(() => {
            return img.parentElement?.offsetWidth ?? 0;
        }).then((newWidth) => {
            fd.mutate(() => {
                imgWidth.value = newWidth;
            });
        });
    });
    const imageResizeObserver2 = imageResizeObserver.value;
    waitForNodePresent(
        "$refs.img",
        () => img.value,
        (node) => imageResizeObserver2.observe(node)
    );
});

const windowInnerWidth = getWindowInnerWidth();

const actualScale = computed(() => {
    if (imgWidth.value === 0) return 1;
    return imgWidth.value / windowInnerWidth.value;
});

const imageResizeObserver = ref<null | ResizeObserver>(null);
const rootResizeObserver = ref<null | ResizeObserver>(null);
const imgWidth = ref(0);
const elWidth = ref(0);
const imageSize = ref({
    width: imageWidth,
    height: imageHeight,
});

function suffix(n: number) {
    const str = Math.floor(n).toString();
    const lastChar = str[str.length - 1];
    switch (str.substring(str.length - 2, str.length)) {
        case "11":
        case "12":
        case "13":
        case "14":
        case "15":
        case "16":
        case "17":
        case "18":
        case "19":
            return str + "th";
    }
    switch (lastChar) {
        case "0":
            return n >= 10 ? str + "th" : str;
        case "1":
            return str + "st";
        case "2":
            return str + "nd";
        case "3":
            return str + "rd";
        default:
            return str + "th";
    }
}
function waitForNodePresent(
    name: string,
    fnc: () => Node | undefined,
    final: (arg0: Element) => void,
    attempt = 0
) {
    const node = fnc();
    if (node == null) {
        // console.log(
        //     `Waiting for ${name} to be present${
        //         attempt > 0 ? ` for the ${suffix(attempt + 1)}. time` : ""
        //     }..`
        // );
        nextTick(function () {
            waitForNodePresent(name, fnc, final, attempt + 1);
        });
        return;
    }
    if (node.nodeType !== 1) {
        // console.log(
        //     `Waiting for ${name} to be present${
        //         attempt > 0 ? ` for the ${suffix(attempt + 1)}. time` : ""
        //     } as a Element..`
        // );
        nextTick(function () {
            waitForNodePresent(name, fnc, final, attempt + 1);
        });
        return;
    }
    // console.log(
    //     `Received ${name} ${
    //         attempt > 0
    //             ? `after ${suffix(attempt + 1)} attempt`
    //             : "on first try"
    //     }`
    // );
    final(node as Element);
}
</script>
